import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'

const API_BASE = '/api'

const CATEGORY_COLORS = {
    'Surveillance & CCTV': '#2563eb',
    'Communications & Intercom': '#7c3aed',
    'Forensics & Investigation': '#059669',
    'Tactical & Protection': '#dc2626',
    'IT & Cybersecurity': '#0891b2',
    'Access Control': '#d97706',
    'Fire & Rescue': '#ea580c',
    'Detection & Scanning': '#65a30d',
    'CBRN & Radiation': '#be123c',
    'Drones & UAV': '#0284c7',
    'Vehicles & Mobility': '#4338ca',
    'Biometrics & Facial Recognition': '#0d9488',
    'Training & Simulation': '#9333ea',
    'License Plate Recognition': '#0369a1',
    'Command & Control': '#b45309',
    'Other / Uncategorized': '#6b7280'
}

const truncate = (str, n) => str && str.length > n ? str.slice(0, n) + '\u2026' : (str || '')

export default function Lexis() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    // Filter state from URL params
    const [q, setQ] = useState(searchParams.get('q') || '')
    const [selectedCategories, setSelectedCategories] = useState(
        searchParams.getAll('category').map(Number)
    )
    const [selectedClients, setSelectedClients] = useState(
        searchParams.getAll('client')
    )
    const [selectedVendors, setSelectedVendors] = useState(
        searchParams.getAll('vendor').map(Number)
    )
    const [sort, setSort] = useState(searchParams.get('sort') || 'az')
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1)

    // Data state
    const [entries, setEntries] = useState([])
    const [total, setTotal] = useState(0)
    const [pages, setPages] = useState(0)
    const [filterCounts, setFilterCounts] = useState({ categories: [], clients: [], vendors: [] })
    const [loading, setLoading] = useState(true)

    // UI state
    const [searchInput, setSearchInput] = useState(q)
    const [collapsedSections, setCollapsedSections] = useState({})
    const [showMoreSections, setShowMoreSections] = useState({})

    // Build URL params from filter state
    const updateURL = useCallback((updates) => {
        const params = new URLSearchParams()
        const newQ = updates.q !== undefined ? updates.q : q
        const newCats = updates.categories !== undefined ? updates.categories : selectedCategories
        const newClients = updates.clients !== undefined ? updates.clients : selectedClients
        const newVendors = updates.vendors !== undefined ? updates.vendors : selectedVendors
        const newSort = updates.sort !== undefined ? updates.sort : sort
        const newPage = updates.page !== undefined ? updates.page : page

        if (newQ) params.set('q', newQ)
        for (const c of newCats) params.append('category', String(c))
        for (const c of newClients) params.append('client', c)
        for (const v of newVendors) params.append('vendor', String(v))
        if (newSort !== 'az') params.set('sort', newSort)
        if (newPage > 1) params.set('page', String(newPage))

        setSearchParams(params, { replace: true })
    }, [q, selectedCategories, selectedClients, selectedVendors, sort, page, setSearchParams])

    // Fetch entries
    const fetchEntries = useCallback(async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams()
            if (q) params.set('q', q)
            for (const c of selectedCategories) params.append('category', String(c))
            for (const c of selectedClients) params.append('client', c)
            for (const v of selectedVendors) params.append('vendor', String(v))
            params.set('page', String(page))
            params.set('sort', sort)
            params.set('limit', '24')

            const res = await fetch(`${API_BASE}/lexis/entries-filtered?${params}`)
            const data = await res.json()
            setEntries(data.entries || [])
            setTotal(data.total || 0)
            setPages(data.pages || 0)
            setFilterCounts(data.filter_counts || { categories: [], clients: [], vendors: [] })
        } catch (e) {
            console.error('Fetch entries error:', e)
        }
        setLoading(false)
    }, [q, selectedCategories, selectedClients, selectedVendors, page, sort])

    useEffect(() => {
        fetchEntries()
    }, [fetchEntries])

    // Filter handlers
    const updateFilter = (key, value) => {
        if (key === 'q') {
            setQ(value)
            setPage(1)
            updateURL({ q: value, page: 1 })
        } else if (key === 'sort') {
            setSort(value)
            setPage(1)
            updateURL({ sort: value, page: 1 })
        }
    }

    const toggleCategory = (catId) => {
        const next = selectedCategories.includes(catId)
            ? selectedCategories.filter(c => c !== catId)
            : [...selectedCategories, catId]
        setSelectedCategories(next)
        setPage(1)
        updateURL({ categories: next, page: 1 })
    }

    const toggleClient = (code) => {
        const next = selectedClients.includes(code)
            ? selectedClients.filter(c => c !== code)
            : [...selectedClients, code]
        setSelectedClients(next)
        setPage(1)
        updateURL({ clients: next, page: 1 })
    }

    const toggleVendor = (vendorId) => {
        const next = selectedVendors.includes(vendorId)
            ? selectedVendors.filter(v => v !== vendorId)
            : [...selectedVendors, vendorId]
        setSelectedVendors(next)
        setPage(1)
        updateURL({ vendors: next, page: 1 })
    }

    const clearAllFilters = () => {
        setQ('')
        setSearchInput('')
        setSelectedCategories([])
        setSelectedClients([])
        setSelectedVendors([])
        setPage(1)
        setSort('az')
        updateURL({ q: '', categories: [], clients: [], vendors: [], page: 1, sort: 'az' })
    }

    const hasActiveFilters = q || selectedCategories.length || selectedClients.length || selectedVendors.length

    const toggleSection = (section) => {
        setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }))
    }

    const toggleShowMore = (section) => {
        setShowMoreSections(prev => ({ ...prev, [section]: !prev[section] }))
    }

    return (
        <div style={{ display: 'flex', gap: 0, minHeight: '100vh' }}>

            {/* LEFT SIDEBAR — 280px fixed */}
            <div style={{
                width: 280, minWidth: 280, padding: 16,
                borderRight: '1px solid #333', overflowY: 'auto',
                position: 'sticky', top: 0, height: '100vh',
                background: '#0f172a'
            }}>

                {/* Search */}
                <input
                    placeholder="Search products..."
                    value={searchInput}
                    onChange={e => { setSearchInput(e.target.value); setQ(e.target.value); setPage(1); updateURL({ q: e.target.value, page: 1 }) }}
                    style={{
                        width: '100%', marginBottom: 16, padding: '8px 12px',
                        borderRadius: 6, background: '#1e293b', border: '1px solid #334155',
                        color: '#f1f5f9', fontSize: 13, outline: 'none', boxSizing: 'border-box'
                    }}
                />

                {/* Active filters summary */}
                {hasActiveFilters && (
                    <button
                        onClick={clearAllFilters}
                        style={{
                            width: '100%', padding: '8px 12px', marginBottom: 16,
                            background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: 6, color: '#ef4444', cursor: 'pointer', fontSize: 12, fontWeight: 600
                        }}
                    >
                        \u2715 Clear all filters
                    </button>
                )}

                {/* CATEGORIES */}
                <FilterSection
                    title="Category"
                    collapsed={collapsedSections.categories}
                    onToggle={() => toggleSection('categories')}
                >
                    {(showMoreSections.categories
                        ? filterCounts.categories
                        : filterCounts.categories.slice(0, 8)
                    ).map(cat => (
                        <FilterCheckbox
                            key={cat.id}
                            label={cat.name}
                            count={cat.count}
                            checked={selectedCategories.includes(cat.id)}
                            color={CATEGORY_COLORS[cat.name] || cat.color || '#6b7280'}
                            onChange={() => toggleCategory(cat.id)}
                        />
                    ))}
                    {filterCounts.categories.length > 8 && (
                        <button
                            onClick={() => toggleShowMore('categories')}
                            style={{ background: 'none', border: 'none', color: '#d4af37', cursor: 'pointer', fontSize: 12, padding: '4px 0' }}
                        >
                            {showMoreSections.categories ? 'Show less' : `Show all (${filterCounts.categories.length})`}
                        </button>
                    )}
                </FilterSection>

                {/* CLIENT AGENCIES */}
                <FilterSection
                    title="Client Agency"
                    collapsed={collapsedSections.clients}
                    onToggle={() => toggleSection('clients')}
                >
                    {(showMoreSections.clients
                        ? filterCounts.clients
                        : filterCounts.clients.slice(0, 8)
                    ).map(c => (
                        <FilterCheckbox
                            key={c.code_name}
                            label={`[${c.code_name}] ${c.full_name_en || ''}`}
                            count={c.count}
                            checked={selectedClients.includes(c.code_name)}
                            onChange={() => toggleClient(c.code_name)}
                        />
                    ))}
                    {filterCounts.clients.length > 8 && (
                        <button
                            onClick={() => toggleShowMore('clients')}
                            style={{ background: 'none', border: 'none', color: '#d4af37', cursor: 'pointer', fontSize: 12, padding: '4px 0' }}
                        >
                            {showMoreSections.clients ? 'Show less' : `Show all (${filterCounts.clients.length})`}
                        </button>
                    )}
                </FilterSection>

                {/* VENDORS */}
                <FilterSection
                    title="Vendor"
                    collapsed={collapsedSections.vendors}
                    onToggle={() => toggleSection('vendors')}
                >
                    {(showMoreSections.vendors
                        ? filterCounts.vendors
                        : filterCounts.vendors.slice(0, 8)
                    ).map(v => (
                        <FilterCheckbox
                            key={v.id}
                            label={v.name}
                            count={v.count}
                            checked={selectedVendors.includes(v.id)}
                            onChange={() => toggleVendor(v.id)}
                        />
                    ))}
                    {filterCounts.vendors.length > 8 && (
                        <button
                            onClick={() => toggleShowMore('vendors')}
                            style={{ background: 'none', border: 'none', color: '#d4af37', cursor: 'pointer', fontSize: 12, padding: '4px 0' }}
                        >
                            {showMoreSections.vendors ? 'Show less' : `Show all (${filterCounts.vendors.length})`}
                        </button>
                    )}
                </FilterSection>

            </div>

            {/* MAIN CONTENT */}
            <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>

                {/* Top bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ color: '#94a3b8', fontSize: 14 }}>
                        <strong style={{ color: '#f1f5f9' }}>{total}</strong> products
                    </span>
                    <select
                        value={sort}
                        onChange={e => updateFilter('sort', e.target.value)}
                        style={{
                            padding: '8px 12px', background: '#1e293b',
                            border: '1px solid #334155', borderRadius: 6,
                            color: '#f1f5f9', fontSize: 13, cursor: 'pointer'
                        }}
                    >
                        <option value="az">A \u2192 Z</option>
                        <option value="za">Z \u2192 A</option>
                        <option value="recent">Recently Updated</option>
                    </select>
                </div>

                {/* Grid */}
                {loading ? (
                    <SkeletonGrid />
                ) : entries.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>\uD83D\uDCC2</div>
                        <p style={{ fontSize: 16, marginBottom: 16 }}>No results found</p>
                        {hasActiveFilters && (
                            <button
                                onClick={clearAllFilters}
                                style={{
                                    padding: '10px 20px', background: 'rgba(212,175,55,0.15)',
                                    border: '1px solid rgba(212,175,55,0.3)', borderRadius: 8,
                                    color: '#d4af37', cursor: 'pointer', fontSize: 13
                                }}
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                        {entries.map(entry => (
                            <EntryCard key={entry.id} entry={entry} />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24, alignItems: 'center' }}>
                        <button
                            disabled={page <= 1}
                            onClick={() => { setPage(p => p - 1); updateURL({ page: page - 1 }) }}
                            style={{
                                padding: '8px 16px', background: '#1e293b',
                                border: '1px solid #334155', borderRadius: 6,
                                color: page <= 1 ? '#475569' : '#94a3b8',
                                cursor: page <= 1 ? 'default' : 'pointer', fontSize: 13
                            }}
                        >
                            \u2190 Prev
                        </button>
                        <span style={{ color: '#94a3b8', fontSize: 13 }}>Page {page} of {pages}</span>
                        <button
                            disabled={page >= pages}
                            onClick={() => { setPage(p => p + 1); updateURL({ page: page + 1 }) }}
                            style={{
                                padding: '8px 16px', background: '#1e293b',
                                border: '1px solid #334155', borderRadius: 6,
                                color: page >= pages ? '#475569' : '#94a3b8',
                                cursor: page >= pages ? 'default' : 'pointer', fontSize: 13
                            }}
                        >
                            Next \u2192
                        </button>
                    </div>
                )}

            </div>
        </div>
    )
}

// ─── Sub-components ───

function FilterSection({ title, collapsed, onToggle, children }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <button
                onClick={onToggle}
                style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
                    background: 'none', border: 'none', color: '#d4af37', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, padding: '8px 0'
                }}
            >
                {title}
                <span style={{
                    fontSize: 10, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
                    transition: 'transform 0.15s'
                }}>\u25BC</span>
            </button>
            {!collapsed && (
                <div style={{ paddingLeft: 4 }}>
                    {children}
                </div>
            )}
        </div>
    )
}

function FilterCheckbox({ label, count, checked, onChange, color }) {
    return (
        <label
            style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
                cursor: 'pointer', fontSize: 12, color: checked ? '#f1f5f9' : '#94a3b8'
            }}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                style={{ accentColor: color || '#d4af37' }}
            />
            {color && (
                <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: color, flexShrink: 0
                }} />
            )}
            <span style={{
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', maxWidth: 160
            }}>{truncate(label, 28)}</span>
            <span style={{ color: '#64748b', fontSize: 11 }}>({count})</span>
        </label>
    )
}

function EntryCard({ entry }) {
    const navigate = useNavigate()
    const { user } = useAuth()
    const catColor = CATEGORY_COLORS[entry.category_name] || '#6b7280'
    const clientCodes = (entry.clients || []).slice(0, 3)
    const clientMore = (entry.clients || []).length - 3
    const tags = (entry.tags || []).slice(0, 3)
    const [enriching, setEnriching] = useState(false)

    const handleWebEnrich = async (e) => {
        e.stopPropagation()
        setEnriching(true)
        try {
            const token = localStorage.getItem('accessToken')
            const res = await fetch(`/api/lexis/entries/${entry.id}/web-enrich`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            })
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: 'Enrichment failed' }))
                throw new Error(err.detail || 'Enrichment failed')
            }
            alert('✓ Enriched')
            // Reload the page data
            window.location.reload()
        } catch (e) {
            alert(`Error: ${e.message}`)
        }
        setEnriching(false)
    }

    return (
        <div
            onClick={() => navigate(`/lexis/${entry.id}`)}
            style={{
                background: '#1e293b', borderRadius: 12, padding: 20,
                border: '1px solid #334155', cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s'
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)'
                e.currentTarget.style.borderColor = '#d4af37'
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
                e.currentTarget.style.borderColor = '#334155'
            }}
        >
            {/* Title */}
            <h3 style={{
                margin: '0 0 8px', fontSize: 15, lineHeight: 1.3, color: '#f1f5f9',
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
            }}>
                {entry.title}
            </h3>

            {/* Category badge */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
                <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: catColor, display: 'inline-block'
                }} />
                <span style={{
                    color: catColor, fontSize: 11, fontWeight: 600
                }}>{entry.category_name || '—'}</span>
            </div>

            {/* Overview */}
            <p style={{
                color: '#94a3b8', fontSize: 13, margin: '0 0 12px',
                lineHeight: 1.4
            }}>
                {truncate(entry.overview_en, 100)}
            </p>

            {/* Client codes */}
            {clientCodes.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                    {clientCodes.map((c, i) => (
                        <span key={i} style={{
                            background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8',
                            padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600
                        }}>[{c.code_name}]</span>
                    ))}
                    {clientMore > 0 && (
                        <span style={{ color: '#64748b', fontSize: 10 }}>+{clientMore}</span>
                    )}
                </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                    {tags.map((t, i) => (
                        <span key={i} style={{
                            background: 'rgba(16, 185, 129, 0.1)', color: '#34d399',
                            padding: '1px 6px', borderRadius: 4, fontSize: 10
                        }}>{t}</span>
                    ))}
                </div>
            )}

            {/* Vendor + Web Enrich button */}
            {entry.vendor_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#64748b' }}>
                    <span>{entry.vendor_name}</span>
                    {user && (user.role === 'admin' || user.role === 'editor') && (
                        <button
                            onClick={handleWebEnrich}
                            disabled={enriching}
                            style={{
                                padding: '2px 8px',
                                background: enriching ? '#374151' : 'rgba(30, 64, 175, 0.8)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                borderRadius: 4,
                                color: '#93c5fd',
                                cursor: enriching ? 'not-allowed' : 'pointer',
                                fontSize: 10,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {enriching ? '🔄...' : '🔍 Enrich'}
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

function SkeletonGrid() {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{
                    background: 'rgba(30, 41, 59, 0.4)', borderRadius: 12, padding: 20,
                    height: 150, animation: 'pulse 1.5s infinite'
                }} />
            ))}
        </div>
    )
}
